import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Modal, useAuth } from '@byscott-io/corebyscott';
import { groups, groupUsers, groupStudents } from '../api/client';

const ROLES = [
  { value: 'leader', label: 'Leader' },
  { value: 'co_leader', label: 'Co-Leader' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'assistant', label: 'Assistant' },
  { value: 'observer', label: 'Observer' },
];

export default function GroupPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', role: 'teacher', can_edit: false, can_assess: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGroup();
  }, [id]);

  const loadGroup = async () => {
    try {
      const response = await groups.get(id);
      setGroup(response.data);
    } catch (err) {
      console.error('Failed to load group:', err);
      setError('Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableStudents = async () => {
    try {
      const response = await groupStudents.available(id);
      setAvailableStudents(response.data);
    } catch (err) {
      console.error('Failed to load available students:', err);
    }
  };

  const handleAddStudent = async (studentId) => {
    setSaving(true);
    try {
      await groupStudents.add(id, studentId);
      setShowAddStudent(false);
      loadGroup();
    } catch (err) {
      console.error('Failed to add student:', err);
      setError(err.response?.data?.error || 'Failed to add student');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!confirm('Remove this student from the group?')) return;

    try {
      await groupStudents.remove(id, studentId);
      loadGroup();
    } catch (err) {
      console.error('Failed to remove student:', err);
      setError('Failed to remove student');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMember.email.trim()) return;

    setSaving(true);
    try {
      await groupUsers.create(id, newMember.email, {
        role: newMember.role,
        can_edit: newMember.can_edit,
        can_assess: newMember.can_assess,
      });
      setNewMember({ email: '', role: 'teacher', can_edit: false, can_assess: true });
      setShowAddMember(false);
      loadGroup();
    } catch (err) {
      console.error('Failed to add member:', err);
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (groupUserId) => {
    if (!confirm('Remove this member from the group?')) return;

    try {
      await groupUsers.delete(id, groupUserId);
      loadGroup();
    } catch (err) {
      console.error('Failed to remove member:', err);
      setError('Failed to remove member');
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirm('Are you sure you want to delete this group? This cannot be undone.')) return;

    try {
      await groups.delete(id);
      navigate('/app/groups');
    } catch (err) {
      console.error('Failed to delete group:', err);
      setError('Failed to delete group');
    }
  };

  const openAddStudentModal = () => {
    loadAvailableStudents();
    setShowAddStudent(true);
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-red-500">Group not found</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-4">
        <Link to="/app/groups" className="text-sm text-indigo-600 hover:text-indigo-500">
          &larr; Back to Groups
        </Link>
      </div>

      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{group.name}</h1>
          {group.description && (
            <p className="mt-1 text-sm text-gray-500">{group.description}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            {group.students?.length || 0} student{(group.students?.length || 0) !== 1 ? 's' : ''} |{' '}
            {group.users?.length || 0} member{(group.users?.length || 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Link
            to={`/app/groups/${id}/analytics`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Analytics
          </Link>
          {isAdmin && (
            <button
              type="button"
              onClick={handleDeleteGroup}
              className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              Delete Group
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="mt-2 text-sm text-red-500 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Students Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Students</h2>
          <button
            type="button"
            onClick={openAddStudentModal}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Add Student
          </button>
        </div>

        {group.students?.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg shadow">
            <span className="text-4xl">🎓</span>
            <p className="mt-2 text-sm text-gray-500">No students in this group yet.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <ul className="divide-y divide-gray-200">
              {group.students?.map((student) => (
                <li key={student.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Link
                        to={`/app/students/${student.id}`}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        {student.full_name}
                      </Link>
                      <p className="text-sm text-gray-500">
                        {student.family_name}
                        {student.grade_level && ` | Grade ${student.grade_level}`}
                        {student.age && ` | ${student.age} years old`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveStudent(student.id)}
                      className="text-sm text-red-600 hover:text-red-500"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Members Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Group Members</h2>
          <button
            type="button"
            onClick={() => setShowAddMember(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Add Member
          </button>
        </div>

        {group.users?.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg shadow">
            <span className="text-4xl">👤</span>
            <p className="mt-2 text-sm text-gray-500">No members in this group yet.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <ul className="divide-y divide-gray-200">
              {group.users?.map((member) => (
                <li key={member.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{member.full_name}</p>
                      <p className="text-sm text-gray-500">{member.email}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                        {ROLES.find((r) => r.value === member.role)?.label || member.role}
                      </span>
                      {member.can_assess && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Can Assess
                        </span>
                      )}
                      {member.can_edit && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Can Edit
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-sm text-red-600 hover:text-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      <Modal
        open={showAddStudent}
        onClose={() => setShowAddStudent(false)}
        title="Add Student to Group"
        size="lg"
        footer={
          <button
            type="button"
            onClick={() => setShowAddStudent(false)}
            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
          >
            Close
          </button>
        }
      >
              <p className="mb-4 text-sm text-gray-500">Select a student to add to this group.</p>

              {availableStudents.length === 0 ? (
                <div className="mt-4 text-center py-8">
                  <p className="text-sm text-gray-500">All available students are already in this group.</p>
                </div>
              ) : (
                <div className="mt-4 max-h-96 overflow-y-auto">
                  <ul className="divide-y divide-gray-200">
                    {availableStudents.map((student) => (
                      <li key={student.id} className="py-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{student.full_name}</p>
                            <p className="text-sm text-gray-500">
                              {student.family_name}
                              {student.grade_level && ` | Grade ${student.grade_level}`}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddStudent(student.id)}
                            disabled={saving}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
      </Modal>

      {/* Add Member Modal */}
      <Modal
        open={showAddMember}
        onClose={() => setShowAddMember(false)}
        title="Add Group Member"
        size="lg"
      >
              <form onSubmit={handleAddMember}>
                <p className="text-sm text-gray-500">Add a user to this group by their email address.</p>

                <div className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="member-email" className="block text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="member-email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="user@example.com"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label htmlFor="member-role" className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="member-role"
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newMember.can_assess}
                        onChange={(e) => setNewMember({ ...newMember, can_assess: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Can administer assessments</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newMember.can_edit}
                        onChange={(e) => setNewMember({ ...newMember, can_edit: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Can edit group (add/remove students)</span>
                    </label>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={saving || !newMember.email.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                  >
                    {saving ? 'Adding...' : 'Add Member'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddMember(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
      </Modal>
    </div>
  );
}
